/**
 * Date range presets for the paid media dashboard filter bar.
 *
 * Why this exists: Marketers repeatedly filter dashboards by common relative
 * date ranges (last 7 days, last 3 months, etc.). Instead of manual date math,
 * presets compute the correct start/end dates at selection time. The computed
 * dates (not the preset label) are the source of truth stored in URL params.
 */

export const formatDate = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const today = (): Date => new Date();

export const daysAgo = (n: number): Date => {
  const d = today();
  d.setDate(d.getDate() - n);
  return d;
};

/**
 * Returns a date N months in the past, clamping to month-end when necessary.
 * For example, if today is March 31 and n=1, naive setMonth(1) would overflow
 * Feb 31 to Mar 3. This function detects the overflow and clamps to the last
 * day of the target month (Feb 28 or 29).
 */
export const monthsAgo = (n: number): Date => {
  const d = today();
  const targetMonth = d.getMonth() - n;
  d.setMonth(targetMonth);
  if (d.getMonth() !== ((targetMonth % 12) + 12) % 12) {
    d.setDate(0);
  }
  return d;
};

export interface DatePreset {
  label: string;
  value: string;
  getRange: () => { startDate: string; endDate: string };
}

export const DATE_PRESETS: DatePreset[] = [
  {
    label: 'Last 7 Days',
    value: 'last7',
    getRange: () => ({ startDate: formatDate(daysAgo(7)), endDate: formatDate(today()) }),
  },
  {
    label: 'Last 14 Days',
    value: 'last14',
    getRange: () => ({ startDate: formatDate(daysAgo(14)), endDate: formatDate(today()) }),
  },
  {
    label: 'Last 30 Days',
    value: 'last30',
    getRange: () => ({ startDate: formatDate(daysAgo(30)), endDate: formatDate(today()) }),
  },
  {
    label: 'Last 3 Months',
    value: 'last3m',
    getRange: () => ({ startDate: formatDate(monthsAgo(3)), endDate: formatDate(today()) }),
  },
  {
    label: 'Last 6 Months',
    value: 'last6m',
    getRange: () => ({ startDate: formatDate(monthsAgo(6)), endDate: formatDate(today()) }),
  },
  {
    label: 'Past Year',
    value: 'pastYear',
    getRange: () => ({ startDate: formatDate(monthsAgo(12)), endDate: formatDate(today()) }),
  },
  {
    label: 'Custom',
    value: 'custom',
    getRange: () => ({ startDate: '', endDate: '' }),
  },
];

export const DEFAULT_PRESET = 'last7';
